import { ModuleTypeEnum, ModuleColumnDefinition } from '../module';
import { FieldTypeEnum } from '../custom-field.types';
import { Project } from '../project';
import { EntityTerminology } from '../organization-settings';
import { STANDARD_COLUMN_WIDTHS, getFieldTypeWidth } from '../column-widths';
import { DEFAULT_PROJECT_STATUSES } from '../crm-stage';

/**
 * Creates standard column definitions for the Project module with proper terminology
 * @param terminology - The entity terminology to use for labels
 */
export function createProjectStandardColumns(_terminology?: EntityTerminology): ModuleColumnDefinition[] {
  return [
  {
    id: 'name',
    name: 'name' as keyof Project, // Type-safe key reference
    label: 'Project Name',
    description: 'Name of the project',
    isVisible: true,
    isRequired: true,
    isSystem: true, // Cannot be disabled
    isFixed: true, // This column cannot be moved
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.VERY_WIDE,
  },
  {
    id: 'entityId',
    name: 'entityId' as keyof Project, // Type-safe key reference
    label: 'Linked Record',
    description: 'Record associated with the project',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'code',
    name: 'code' as keyof Project, // Type-safe key reference
    label: 'Code',
    description: 'Project code (2-3 uppercase letters)',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'status',
    name: 'status' as keyof Project, // Type-safe key reference
    label: 'Status',
    description: 'Current status of the project',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    // Use CRM stage defaults - these are lowercase IDs (active, not-started, etc.)
    // Actual options are fetched from API and may be customized per org
    options: DEFAULT_PROJECT_STATUSES.filter(s => !s.isHidden).map(status => ({
      value: status.id,
      label: status.name,
      color: status.color,
    })),
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'taskCount',
    name: '_count.Task' as keyof Project, // Type-safe key reference for count
    label: 'Tasks',
    description: 'Number of tasks in this project',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'members',
    name: 'projectMembers' as keyof Project, // Type-safe key reference
    label: 'Members',
    description: 'Project team members',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 4.5, // Between tasks and description
    fieldType: 'PROJECT_MEMBERS' as FieldTypeEnum, // Special field type for project members display
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'tags',
    name: 'tags' as keyof Project,
    label: 'Tags',
    description: 'Tags for organizing and categorizing the project',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 90, // Positioned near end of standard columns, custom fields come after at 91+
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'description',
    name: 'description' as keyof Project, // Type-safe key reference
    label: 'Description',
    description: 'Description of the project',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.TEXT,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'projectType',
    name: 'projectType' as keyof Project, // Type-safe key reference
    label: 'Project Type',
    description: 'Type of project (BASIC, KANBAN, SCRUM, WATERFALL)',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 6,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'startDate',
    name: 'startDate' as keyof Project, // Type-safe key reference
    label: 'Start Date',
    description: 'Project start date',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.DATE,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'endDate',
    name: 'endDate' as keyof Project, // Type-safe key reference
    label: 'End Date',
    description: 'Project end date',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.DATE,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof Project, // Type-safe key reference
    label: 'Created Date',
    description: 'Date when project was created',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 9,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'createdById',
    name: 'createdById' as keyof Project, // Type-safe key reference
    label: 'Created By',
    description: 'User who created this project',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 9.5,
    fieldType: FieldTypeEnum.enum.USER,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'updatedAt',
    name: 'updatedAt' as keyof Project, // Type-safe key reference
    label: 'Last Updated',
    description: 'Date when project was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  ];
}

/**
 * Default standard column definitions for the Project module
 * Uses default 'entity' terminology
 */
export const PROJECT_STANDARD_COLUMNS: ModuleColumnDefinition[] = createProjectStandardColumns();

/**
 * Helper function to get all available columns for a module, 
 * combining standard and custom fields
 */
export function getProjectColumns(
  customFields: any[] = [], 
  visibilityPreferences?: Record<string, boolean>,
  terminology?: EntityTerminology
): ModuleColumnDefinition[] {
  // Start with standard columns using the provided terminology
  const standardColumns = createProjectStandardColumns(terminology).map(column => ({
    ...column,
    // Override visibility if preferences exist
    isVisible: visibilityPreferences?.[column.id] !== undefined 
      ? !!visibilityPreferences[column.id] // Ensure boolean type with !!
      : column.isVisible
  }));
  
  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    // Use standardized width for custom field columns based on field type
    const widthSettings = getFieldTypeWidth(field.fieldType);

    // Parse dropdown options from settings if available
    const fieldOptions = (field.fieldType === 'DROPDOWN' || field.fieldType === 'MULTISELECT')
      && field.settings?.options && Array.isArray(field.settings.options)
      ? field.settings.options.map((opt: any) => {
          if (typeof opt === 'string') return { value: opt, label: opt };
          return { value: String(opt.value || opt.id || opt.label || ''), label: String(opt.label || opt.name || opt.value || '') };
        })
      : undefined;

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible: visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
        ? !!visibilityPreferences[`custom_${field.fieldName}`] // Ensure boolean type with !!
        : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index, // Custom fields come after tags (order 90)
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
      ...(fieldOptions ? { options: fieldOptions } : {}),
    };
  }) as ModuleColumnDefinition[];
  
  // Combine both types of columns
  return [...standardColumns, ...customColumns];
}

/**
 * Get module type for column definitions
 */
export function getProjectModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.PROJECT;
} 
